import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Sparkles, Home, FolderKanban, Flame, Megaphone,
  AlertTriangle, ArrowUpRight, Zap
} from 'lucide-react';
import { useActiveSubscription } from '@/hooks/useActiveSubscription';
import { formatDistanceToNow } from 'date-fns';

function MiniMeter({ icon: Icon, label, used, quota }: {
  icon: any; label: string; used: number; quota: number;
}) {
  const pct = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
  const remaining = Math.max(0, quota - used);
  const low = quota > 0 && remaining <= Math.ceil(quota * 0.2);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[11px]">
        <span className="flex items-center gap-1 text-muted-foreground">
          <Icon className="h-3 w-3" /> {label}
        </span>
        <span className={`tabular-nums font-medium ${low ? 'text-destructive' : 'text-foreground'}`}>
          {remaining}
        </span>
      </div>
      <Progress value={pct} className="h-1" />
    </div>
  );
}

export function MyPackageWidget() {
  const sub = useActiveSubscription();

  if (sub.isLoading) {
    return (
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5 h-32 animate-pulse bg-muted/40" />
      </Card>
    );
  }

  if (!sub.hasActive) {
    return (
      <Card className="rounded-2xl border-0 shadow-sm border-l-4 border-l-destructive/60">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="rounded-full bg-destructive/10 p-2 shrink-0">
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">No active package</p>
            <p className="text-xs text-muted-foreground">Subscribe to publish listings and unlock features.</p>
          </div>
          <Button asChild size="sm" variant="outline" className="shrink-0 gap-1">
            <Link to="/dashboard/subscription">View plans <ArrowUpRight className="h-3 w-3" /></Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const pkg = sub.primary!;
  const expiresAt = new Date(pkg.expires_at);
  const daysLeft = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  const expiringSoon = daysLeft <= 7 && daysLeft > 0;
  const expired = daysLeft <= 0;

  return (
    <Card className="rounded-2xl border-0 shadow-sm bg-gradient-to-br from-primary/[0.04] to-transparent border border-primary/10">
      <CardContent className="p-4 space-y-3">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="rounded-lg bg-primary/10 p-1.5 shrink-0">
              <Zap className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="font-semibold text-sm truncate">{pkg.package_name}</p>
                <Badge variant="outline" className="text-[10px] capitalize shrink-0">{pkg.audience}</Badge>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {expired
                  ? 'Expired'
                  : expiringSoon
                    ? `Expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`
                    : `Renews ${formatDistanceToNow(expiresAt, { addSuffix: true })}`
                }
              </p>
            </div>
          </div>
          <Badge
            variant={expired ? 'destructive' : expiringSoon ? 'secondary' : 'default'}
            className="text-[10px] shrink-0"
          >
            {expired ? 'Expired' : expiringSoon ? 'Expiring Soon' : 'Active'}
          </Badge>
        </div>

        {/* Mini quota meters */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <MiniMeter icon={Home} label="Listings" used={sub.listings.used} quota={sub.listings.quota} />
          <MiniMeter icon={FolderKanban} label="Projects" used={sub.projects.used} quota={sub.projects.quota} />
          <MiniMeter icon={Flame} label="Boosts" used={sub.boosts.used} quota={sub.boosts.quota} />
          <MiniMeter icon={Megaphone} label="Banners" used={sub.banners.used} quota={sub.banners.totalQuota} />
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Sparkles className="h-3 w-3" />
            <span>{sub.listings.remaining + sub.projects.remaining} credits remaining</span>
          </div>
          <Button asChild size="sm" variant="ghost" className="h-7 text-xs gap-1">
            <Link to="/dashboard/subscription">Manage <ArrowUpRight className="h-3 w-3" /></Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
